import { Capacitor } from '@capacitor/core';
import '@fontsource/inter';
import { Box } from '@mui/joy';
import OneSignalNative from 'onesignal-cordova-plugin';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import OneSignal from 'react-onesignal';
import { useDispatch } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './components/drawer';
import { CohortDeadlineBanner } from './features/mfa/components/CohortDeadlineBanner';
import { Header } from './components/header';
import ScrollToTop from './hooks/ScrollToTop';
import { useUser } from './hooks/useUser';
import { getUser, logoutUser } from './redux/actions/userActions';
import dayjs from './services/dayjs';
import getAuthToken from './services/getAuthToken';
import { setupNativeBackButtonAction } from './utils/handleNativeActions';
import ReactGA from 'react-ga4';

const initializeGA = () => {
  ReactGA.initialize('G-06X7T9QR89');
};

const ONESIGNAL_APP_ID =
  process.env.REACT_APP_ENV === 'development'
    ? '63caeac8-2991-4ac2-9911-c81dc0319bd0'
    : process.env.REACT_APP_ENV === 'staging'
    ? '40fbc256-b6cd-4c41-a1ae-ae1c5e6cbee1'
    : 'a9617ecb-0774-479a-9275-b1dfbd87e566';

export function App() {
  dayjs.configure();
  const userQuery = useUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [onesignalConfigured, setOnesignalConfigured] = useState(false);

  const configureOneSignalNative = async (userId: string) => {
    OneSignalNative.setAppId(ONESIGNAL_APP_ID);

    OneSignalNative.setNotificationOpenedHandler(() => {
      /* notification tap handled by route navigation elsewhere */
    });

    // Prompts the user for notification permissions.
    OneSignalNative.promptForPushNotificationsWithUserResponse(() => {
      // Set external user id
      (window as any).plugins.OneSignal.setExternalUserId(userId);
    });
  };

  const configureOneSignal = async (userId: string) => {
    try {
      await OneSignal.init({
        appId: ONESIGNAL_APP_ID,
      });
      await OneSignal.registerForPushNotifications();
      const isEnabled = await OneSignal.isPushNotificationsEnabled();
      if (!isEnabled) {
        await OneSignal.showNativePrompt();
      }
      const externalUserId = await OneSignal.getExternalUserId();
      if (!externalUserId) {
        await OneSignal.setExternalUserId(userId);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const setup = async () => {
    setupNativeBackButtonAction();

    // Native (Capacitor) reads the JWT from secure Preferences storage —
    // null means truly logged out and we can short-circuit. Web's auth
    // lives in an HttpOnly cookie that JS can't read (Week 8 cutover), so
    // getAuthToken() always returns null there; the real gate on web is
    // the useUser query above, which 401-bounces via the axios interceptor.
    // Without this platform guard, every web login would be undone here:
    // the login response sets the cookie, App mounts, getAuthToken returns
    // null, logoutUser() clears the cookie we just received, and navigate
    // sends the user straight back to /login.
    if (Capacitor.isNativePlatform()) {
      const token = await getAuthToken();
      if (!token) {
        dispatch(logoutUser());
        navigate('/login');
        return;
      }
    }

    if (onesignalConfigured) {
      return;
    }

    Capacitor.isNativePlatform()
      ? await configureOneSignalNative(userQuery.data!._id)
      : await configureOneSignal(userQuery.data!._id);
    setOnesignalConfigured(true);
  };

  useEffect(() => {
    initializeGA();
  }, []);

  useEffect(() => {
    if (!userQuery.isSuccess) {
      return;
    }

    // Legacy
    (async () => {
      await dispatch(getUser());
    })();
    setup();
  }, [userQuery]);

  useEffect(() => {
    if (!userQuery.isSuccess) {
      return;
    }
  }, [dispatch, navigate]);

  if (!userQuery.isSuccess) {
    return null;
  }

  return (
    <Box>
      <ScrollToTop />
      <Sidebar />
      <Header />
      <CohortDeadlineBanner />
      <Toaster />

      <Box
        sx={{
          position: 'absolute',
          width: {
            xs: '100%',
          },
          top: 0,
          height: '100%',
          pt: {
            xs: 'var(--Header-height)',
            lg: 'calc(var(--Header-height) + 1.5rem)',
          },
          paddingLeft: {
            xs: 0,
            lg: 'var(--Sidebar-width)',
          },
          // backgroundColor: '#FBFBF3',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default App;
